import {
  BomColorChangeEvent,
  BomOptionsErrorEvent,
  BomOptionsLoadingCompletedEvent,
  BomOptionsSavingCompletedEvent,
  BomOptionsSavingStartEvent,
  BomOptionsValidationPassedEvent,
  DictionaryKeyDelimiter,
  QuestionType,
  RepresentationType,
  SelectionType,
} from './constants';
import { getChildQuestion, getParentQuestion, getQuestion } from './selectors';

import { cleanUpRecentBomOptionsList } from '../nextgen/actions';
import deepEqual from 'deep-equal';
import { promoStudioUITypeValue } from '../main/constants';
import { updateUniqueOptions } from './actions';

const getLeafQuestionHelper = (state, code) => {
  let question = getQuestion(state, code);
  if (!question || question.questionType === QuestionType.GENERAL) return undefined;

  if (question.questionType !== QuestionType.LEAF) {
    do {
      question = getChildQuestion(state, question.code);
    } while (question && question.questionType !== QuestionType.LEAF);
  }
  return question;
};

const getRootQuestionHelper = (state, code) => {
  let question = getQuestion(state, code);
  if (!question || question.questionType === QuestionType.GENERAL) return undefined;

  if (question.questionType !== QuestionType.ROOT) {
    do {
      question = getParentQuestion(state, question.code);
    } while (question && question.questionType !== QuestionType.ROOT);
  }
  return question;
};

const hasChildQuestionsNeedToReloadUniqueOptions = (state, rootQuestion) => {
  let result = false;
  let childQuestion = getChildQuestion(state, rootQuestion.code);
  while (childQuestion) {
    if (childQuestion.needToReloadUniqueOptions) {
      result = true;
      break;
    }
    childQuestion = getChildQuestion(state, childQuestion.code);
  }
  return result;
};

const hasUniqueOptionsNeedToUpdateInventoryStatus = (childOptionDict, leafQuestion) => {
  const leafOptionDict = childOptionDict[leafQuestion.code] || {};
  return Object.values(leafOptionDict)
    .flat()
    .some((option) => option.isOutOfStock || option.isSoldOut);
};

const keysReducer = (keysAccumulator, currentKeyParts) => {
  const tmpKeys = [];
  keysAccumulator.forEach((key) =>
    currentKeyParts.forEach((keyPart) => tmpKeys.push(key.concat(DictionaryKeyDelimiter, keyPart)))
  );
  return tmpKeys;
};

const generateUniqueOptionsHelper = (state, rootQuestion) => {
  const { childOptions, uniqueOptions, uniqueOptionsBackup } = state;
  const keyParts = [];
  const newUniqueOptionDict = {};

  console.log('=== generateUniqueOptionsHelper() - start ===');
  let hasAncestorQuestionsSelected = false;
  let question = rootQuestion;
  while (question) {
    const { code } = question;

    let options;
    if (question.needToReloadUniqueOptions) {
      const keys = keyParts.reduce(keysReducer);
      console.log('Keys:');
      console.log(keys);

      options = uniqueOptionsBackup[code].map((option) => ({
        ...option,
        isOutOfStock: false,
        isSelected: false,
        isSoldOut: false,
        outOfStockMessage: '',
        quantity: 0,
      }));
      if (hasAncestorQuestionsSelected) {
        const childCodeValueArrays = [];
        const childOptionDict = childOptions[code];
        keys.forEach((key) => {
          if (Object.keys(childOptionDict).includes(key)) {
            childCodeValueArrays.push(childOptionDict[key].map((option) => option.codeValue));
          }
        });
        options = options.filter((option) =>
          childCodeValueArrays.every((codeValueArray) => codeValueArray.includes(option.codeValue))
        );
      }

      uniqueOptions[code]
        .filter((option) => option.isSelected)
        .forEach((oldSelectedOption) => {
          let index = options.findIndex((option) => option.codeValue === oldSelectedOption.codeValue);
          if (index >= 0) {
            options[index] = Object.assign({}, options[index], {
              isSelected: oldSelectedOption.isSelected,
              quantity: oldSelectedOption.quantity,
            });
          }
        });
      if (options.length === 1 && !options[0].isSelected && question.selectionType === SelectionType.SINGLE) {
        options[0].isSelected = true;
      }
      newUniqueOptionDict[code] = options;
      console.log('New Unique Options:');
      console.log(options);
    } else {
      options = uniqueOptions[code];
    }

    if (question.questionType === QuestionType.LEAF) break;

    if (options.some((option) => option.isSelected)) {
      hasAncestorQuestionsSelected = true;
      options = options.filter((option) => option.isSelected);
    }
    keyParts.push(
      options.map((option) => (rootQuestion.isRealParentChildQuestion ? option.codeValue : option.componentId))
    );

    question = getChildQuestion(state, code);
  }
  console.log('Key Parts:');
  console.log(keyParts);

  console.log('New Unique Option Dict:');
  console.log(newUniqueOptionDict);
  console.log('=== generateUniqueOptionsHelper() - end ===');
  return newUniqueOptionDict;
};

const updateInventoryStatusHelper = (state, rootQuestion, regeneratedUniqueOptionDict) => {
  const { childOptions, uniqueOptions } = state;
  const keyParts = [];
  const newUniqueOptionDict = {};

  let ancestorMultipleSelectionQuestionCode = '';
  let allAncestorQuestionsSelected = true;
  let allSingleSelectionQuestionsSelected = true;
  let leafCode = '';

  console.log('=== updateInventoryStatusHelper() - start ===');
  let question = rootQuestion;
  while (question) {
    const { code } = question;

    let options = Object.keys(regeneratedUniqueOptionDict).includes(code)
      ? regeneratedUniqueOptionDict[code]
      : uniqueOptions[code].map((option) => ({
          ...option,
          isOutOfStock: false,
          isSoldOut: false,
          outOfStockMessage: '',
        }));
    if (options.length === 1 && !options[0].isSelected && question.selectionType === SelectionType.SINGLE) {
      options[0].isSelected = true;
    }
    const hasOptionsSelected = options.some((option) => option.isSelected);
    newUniqueOptionDict[code] = options;

    if (question.selectionType === SelectionType.SINGLE && !hasOptionsSelected)
      allSingleSelectionQuestionsSelected = false;

    if (question.questionType === QuestionType.LEAF) {
      leafCode = code;
      break;
    }

    if (question.selectionType === SelectionType.MULTIPLE) ancestorMultipleSelectionQuestionCode = code;
    if (!hasOptionsSelected) allAncestorQuestionsSelected = false;

    if (question.selectionType === SelectionType.SINGLE && hasOptionsSelected)
      options = options.filter((option) => option.isSelected);
    keyParts.push(
      options.map((option) => (rootQuestion.isRealParentChildQuestion ? option.codeValue : option.componentId))
    );

    question = getChildQuestion(state, code);
  }
  console.log('ancestorMultipleSelectionQuestionCode:');
  console.log(ancestorMultipleSelectionQuestionCode);
  console.log('allAncestorQuestionsSelected:');
  console.log(allAncestorQuestionsSelected);
  console.log('allSingleSelectionQuestionsSelected:');
  console.log(allSingleSelectionQuestionsSelected);
  console.log('leafCode:');
  console.log(leafCode);
  console.log('Key Parts:');
  console.log(keyParts);

  if (ancestorMultipleSelectionQuestionCode === '') {
    if (allAncestorQuestionsSelected) {
      for (const uniqueOption of newUniqueOptionDict[leafCode]) {
        const key = keyParts.reduce(keysReducer)[0];
        const leafOptions = childOptions[leafCode][key];
        const targetOption = leafOptions.find((option) => option.codeValue === uniqueOption.codeValue);
        if (targetOption) {
          const { isOutOfStock, isSoldOut, outOfStockMessage } = targetOption;
          uniqueOption.isOutOfStock = isOutOfStock;
          uniqueOption.isSoldOut = isSoldOut;
          uniqueOption.outOfStockMessage = outOfStockMessage;
          if (uniqueOption.isSelected && isSoldOut) {
            uniqueOption.isSelected = false;
            uniqueOption.quantity = 0;
          }
        }
      }
    }
  } else {
    if (allSingleSelectionQuestionsSelected) {
      const keys = keyParts.reduce(keysReducer);
      const leafOptionDict = childOptions[leafCode];
      const selectedLeafUniqueOption = newUniqueOptionDict[leafCode].find((option) => option.isSelected);
      for (const uniqueOption of newUniqueOptionDict[ancestorMultipleSelectionQuestionCode]) {
        const targetKey = keys.find((key) =>
          key
            .split(DictionaryKeyDelimiter)
            .includes(rootQuestion.isRealParentChildQuestion ? uniqueOption.codeValue : uniqueOption.componentId)
        );
        if (targetKey && Object.keys(leafOptionDict).includes(targetKey) && selectedLeafUniqueOption) {
          const targetOption = leafOptionDict[targetKey].find(
            (option) => option.codeValue === selectedLeafUniqueOption.codeValue
          );
          if (targetOption) {
            const { isOutOfStock, isSoldOut, outOfStockMessage } = targetOption;
            uniqueOption.isOutOfStock = isOutOfStock;
            uniqueOption.isSoldOut = isSoldOut;
            uniqueOption.outOfStockMessage = outOfStockMessage;
            if (uniqueOption.isSelected && isSoldOut) {
              uniqueOption.isSelected = false;
              uniqueOption.quantity = 0;
            }
          }
        }
      }
    }
  }

  console.log('New Unique Option Dict:');
  console.log(newUniqueOptionDict);
  console.log('=== updateInventoryStatusHelper() - end ===');
  return newUniqueOptionDict;
};

export const reloadUniqueOptions = (question, updateInventoryStatusOnly = false) => {
  return (dispatch, getState) => {
    if (
      !question ||
      question.questionType === QuestionType.GENERAL ||
      question.representationType !== RepresentationType.GENERAL
    )
      return;

    const state = getState();
    const { childOptions, uniqueOptions } = state;

    const rootQuestion =
      question.questionType === QuestionType.ROOT ? question : getRootQuestionHelper(state, question.code);
    const needToReloadUniqueOptions = hasChildQuestionsNeedToReloadUniqueOptions(state, rootQuestion);
    const leafQuestion =
      question.questionType === QuestionType.LEAF ? question : getLeafQuestionHelper(state, question.code);
    const needToUpdateInventoryStatus = hasUniqueOptionsNeedToUpdateInventoryStatus(childOptions, leafQuestion);
    if (!needToReloadUniqueOptions && !needToUpdateInventoryStatus) return;

    console.log('========== reloadUniqueOptions() - start ==========');
    let newUniqueOptionDict =
      needToReloadUniqueOptions && !updateInventoryStatusOnly ? generateUniqueOptionsHelper(state, rootQuestion) : {};
    newUniqueOptionDict = updateInventoryStatusHelper(state, rootQuestion, newUniqueOptionDict);

    const equalsToLastUniqueOptions = deepEqual(newUniqueOptionDict, uniqueOptions);
    console.log('=== Compare unique options - start ===');
    console.log('Last Unique Option Dict:');
    console.log(uniqueOptions);
    console.log('New Unique Option Dict:');
    console.log(newUniqueOptionDict);
    console.log('Be equal?');
    console.log(equalsToLastUniqueOptions);
    console.log('=== Compare unique options - end ===');
    console.log('========== reloadUniqueOptions() - end ==========');

    if (Object.entries(newUniqueOptionDict).length > 0 && !equalsToLastUniqueOptions) {
      dispatch(updateUniqueOptions(newUniqueOptionDict));

      const { nextgenUi } = state;
      const { productDetailsInterfaceBuffer } = nextgenUi;
      const { recentBomOptions } = productDetailsInterfaceBuffer;
      if (recentBomOptions.length > 0) {
        dispatch(
          cleanUpRecentBomOptionsList(
            (newUniqueOptionDict[recentBomOptions[0].code] || []).filter((option) => option.isSelected)
          )
        );
      }
    }
  };
};

export const updateBomUniqueOptionsForParentChildQuestions = (updateInventoryStatusOnly = false) => {
  return (dispatch, getState) => {
    const state = getState();
    state.questions
      .filter(
        (question) =>
          question.questionType === QuestionType.ROOT && question.representationType === RepresentationType.GENERAL
      )
      .forEach((question) => {
        dispatch(reloadUniqueOptions(question, updateInventoryStatusOnly));
      });
  };
};

export const reloadChildUniqueOptionsForTecSysItem = (parentQuestion, selectedParentOption) => {
  return (dispatch, getState) => {
    if (
      !parentQuestion ||
      parentQuestion.questionType !== QuestionType.ROOT ||
      parentQuestion.representationType !== RepresentationType.GENERAL ||
      parentQuestion.selectionType !== SelectionType.SINGLE ||
      !selectedParentOption
    )
      return;

    const state = getState();
    const { childOptions, specs, uniqueOptions } = state;

    const { isTecSysItem, promoStudioUIType } = specs;
    if (promoStudioUIType !== promoStudioUITypeValue.LiteUI || !isTecSysItem) return;

    const childQuestion = getChildQuestion(state, parentQuestion.code);
    if (!childQuestion) return;

    const { code: childCode } = childQuestion;
    const key = parentQuestion.isRealParentChildQuestion
      ? selectedParentOption.codeValue
      : selectedParentOption.componentId;
    if (!childOptions[childCode] || !childOptions[childCode][key] || !uniqueOptions[childCode]) return;

    const lastSelectedChildOption = uniqueOptions[childCode].find((option) => option.isSelected);
    const lastSelectedCodeValue = !!lastSelectedChildOption ? lastSelectedChildOption.codeValue : '';
    const newChildUniqueOptions = childOptions[childCode][key].map((option) => ({
      ...option,
      componentId: '',
      isSelected: false,
    }));

    if (!!lastSelectedCodeValue) {
      const targetOption = newChildUniqueOptions.find(
        (option) => option.codeValue === lastSelectedCodeValue && !option.isSoldOut
      );
      if (!!targetOption) targetOption.isSelected = true;
    }

    if (newChildUniqueOptions.length > 0 && !newChildUniqueOptions.some((option) => option.isSelected)) {
      const selectableOptions = newChildUniqueOptions.filter((option) => !option.isSelected && !option.isSoldOut);
      if (selectableOptions.length === 1) selectableOptions[0].isSelected = true;
    }

    dispatch(
      updateUniqueOptions({
        [childCode]: newChildUniqueOptions,
      })
    );
  };
};

export const dispatchBomColorChangeEvent = ({ isMultiSelect = false, option }) => {
  const event = new CustomEvent(BomColorChangeEvent, {
    detail: {
      isMultiSelect,
      option: !!option
        ? {
            code: option.code,
            codeValue: option.codeValue,
            colorReference: option.colorReference,
            componentId: option.componentId,
            description: option.description,
            isSelected: option.isSelected,
            itemId: option.itemId,
          }
        : null,
    },
  });
  window.dispatchEvent(event);
};

export const dispatchBomOptionsErrorEvent = (error) => {
  const event = new CustomEvent(BomOptionsErrorEvent, {
    detail: {
      error: error || { message: 'Something went wrong in BomOptions!' },
    },
  });
  window.dispatchEvent(event);
};

export const dispatchBomOptionsLoadingCompletedEvent = () => {
  const event = new CustomEvent(BomOptionsLoadingCompletedEvent, {
    detail: 'Bom options loading completed!',
  });
  window.dispatchEvent(event);
};

export const dispatchBomOptionsSavingCompletedEvent = ({ cartItemId, target }) => {
  const event = new CustomEvent(BomOptionsSavingCompletedEvent, {
    detail: {
      message: 'Bom options saving completed!',
      cartItemId,
      target,
    },
  });
  window.dispatchEvent(event);
};

export const dispatchBomOptionsSavingStartEvent = (target) => {
  const event = new CustomEvent(BomOptionsSavingStartEvent, {
    detail: {
      message: 'Bom options saving starts!',
      target,
    },
  });
  window.dispatchEvent(event);
};

export const dispatchBomOptionsValidationPassedEvent = (target) => {
  const event = new CustomEvent(BomOptionsValidationPassedEvent, {
    detail: {
      message: 'Bom options validation passed!',
      target,
    },
  });
  window.dispatchEvent(event);
};

export const selectDefaultOptionsForBomOptions = () => {
  return (dispatch, getState) => {
    const state = getState();
    const { questions, uniqueOptions } = state;

    const newUniqueOptionDict = {};
    questions
      .filter(
        (question) =>
          !(
            question.questionType === QuestionType.LEAF &&
            question.representationType === RepresentationType.PARENT_CHILD
          )
      )
      .forEach((question) => {
        const { code } = question;
        if (Object.keys(uniqueOptions).includes(code)) {
          const noOptionsSelected =
            uniqueOptions[code].length > 0 && !uniqueOptions[code].some((option) => option.isSelected);
          if (noOptionsSelected) {
            const selectableOptions = uniqueOptions[code].filter((option) => !option.isSelected && !option.isSoldOut);
            if (selectableOptions.length === 1) {
              newUniqueOptionDict[code] = uniqueOptions[code].map((option) =>
                option.codeValue === selectableOptions[0].codeValue ? { ...option, isSelected: true } : { ...option }
              );
            }
          }
        }
      });

    console.log('Invoke selectDefaultOptionsForBomOptions():');
    console.log(newUniqueOptionDict);

    if (Object.entries(newUniqueOptionDict).length > 0) {
      dispatch(updateUniqueOptions(newUniqueOptionDict));
    }
  };
};
